"use client";
import { useAuth } from "@/hooks/useAuth";
import LoginLanding from "@/components/LoginLanding";
import FirstWebsiteForm from "@/components/FirstWebsiteForm";
import { brand } from "@/lib/theme";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { coreDomainSlug } from "@/lib/url";
import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState, AppDispatch } from "@/store";
import { fetchMe, clearUserCache } from "@/store/userSlice";

// Row height formula: favicon container (48px) + vertical padding (20px top + 20px bottom) = 88px total.
// Adjust here if row layout changes so the scroll limit stays exactly at 5 visible rows.
const WEBSITE_ROW_HEIGHT = 88;

export default function WelcomePage() {
  const router = useRouter();
  const { isAuthenticated, isLoading, user, websites, websiteItems, logout, setSelectedWebsite } = useAuth();
  const userState = useSelector((s: RootState) => s.user);
  const dispatch = useDispatch<AppDispatch>();
  const [showAddWebsite, setShowAddWebsite] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);

  // Close user menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!userMenuRef.current) return;
      if (!userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Determine max websites based on account type
  // Admin users have no limit (Infinity), individual users can have 1, others can have 3
  const maxWebsites = user?.type === 'admin' ? Infinity : user?.type === 'individual' ? 1 : 3;
  const canAddMore = (websites?.length || 0) < maxWebsites;

  // Check if there's any domain with pending or running status
  const processingDomain = useMemo(() => {
    const pending = (websiteItems || []).find((item) => item.status === "pending");
    const running = (websiteItems || []).find((item) => item.status === "running");
    
    if (pending) return { status: "pending", name: pending.name };
    if (running) return { status: "running", name: running.name };
    return null;
  }, [websiteItems]);

  // Stable stringify for effect dependency
  const processingDomainKey = processingDomain ? `${processingDomain.status}-${processingDomain.name}` : null;

  // Auto-refresh websites list while a domain is processing (pending/running)
  useEffect(() => {
    if (!processingDomainKey) return;
    let cancelled = false;

    // derived from key
    const currentStatus = processingDomainKey.split('-')[0];
    const isPending = currentStatus === 'pending';
    
    // Timing: keep frequent for pending; much slower for running (result >= 20-30min)
    const FAST_INTERVAL = 15000; // 15s (used for pending)
    const SLOW_INTERVAL = 60000; // 60s (used for pending after initial window)
    const RUNNING_INTERVAL = 5 * 60 * 1000; // 5 minutes when running
    const INITIAL_WINDOW = 2 * 60 * 1000; // first 2 minutes fast (pending only)
    const TOTAL_WINDOW = 10 * 60 * 1000; // stop after 10 minutes
    const start = Date.now();
    let timer: ReturnType<typeof setTimeout> | null = null;
    let runningFetch = false;

    const tick = async () => {
      if (cancelled) return;
      
      runningFetch = true;
      // Force invalidate cache and refetch /auth/me
      dispatch(clearUserCache());
      await dispatch(fetchMe());
      runningFetch = false;

      if (cancelled) return;
      const elapsed = Date.now() - start;
      if (elapsed >= TOTAL_WINDOW) return; // stop polling after cap
      let next: number;
      if (isPending) {
        next = elapsed < INITIAL_WINDOW ? FAST_INTERVAL : SLOW_INTERVAL;
      } else {
        // running
        next = RUNNING_INTERVAL;
      }
      timer = setTimeout(tick, next);
    };

    // Kick off quickly
    timer = setTimeout(tick, 500);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [processingDomainKey, dispatch]);

  // If user token is valid but backend says the user is not registered (403/404), move to registration
  useEffect(() => {
    const code = userState.errorCode;
    if (userState.status === 'failed' && (code === 403 || code === 404)) {
      router.push('/auth/register');
    }
  }, [userState.status, userState.errorCode, router]);

  // Activation check removed — try version has no activation gate

  // Removed redirect to registration when no websites - show form on this page instead

  // Navigate to selected website - triggers prefetch via setSelectedWebsite
  // IMPORTANT: This hook must be before any conditional returns to respect Rules of Hooks
  const goTo = useCallback((w: string) => {
    // Set the website selection first - this triggers the prefetch in useAuth
    setSelectedWebsite(w);
    const slug = coreDomainSlug(w);
    const encoded = encodeURIComponent(slug);
    try { sessionStorage.setItem('requestedWebsiteSlug', slug); } catch {}
    router.push(`/home/${encoded}/overview`);
  }, [setSelectedWebsite, router]);

  // Only show the big loader on the very first load (before we have any user data)
  const hasUserData = !!userState.user; // persist across background refetches
  const showInitialLoader = (isAuthenticated === null) || (!hasUserData && (isLoading || userState.status === 'loading'));
  if (showInitialLoader) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", background: brand.subtle, color: brand.text }}>
        <p>Loading...</p>
      </div>
    );
  }

  // If unregistered, avoid flicker by not rendering landing while redirecting
  if (userState.status === 'failed' && (userState.errorCode === 403 || userState.errorCode === 404)) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <p>Redirecting to registration…</p>
      </div>
    );
  }

  // If account needs activation, show loading while redirecting
  if (userState.status === 'succeeded' && user && user.status === 'pending') {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <p>Redirecting to activation…</p>
      </div>
    );
  }

  if (!isAuthenticated) return <LoginLanding />;

  // Derive first name only
  const firstName = (user?.name || user?.email || "").split(/[\s@._-]+/).filter(Boolean)[0] || "";

  // Status indicator component (same as WebsiteSelector)
  const StatusIndicator = ({ status }: { status?: string }) => {
    if (status === "ready") {
      return (
        <div
          style={{
            width: 16,
            height: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
          title="Ready"
        >
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: "#10B981",
            }}
          />
        </div>
      );
    } else if (status === "running") {
      return (
        <div title="Running" style={{ display: "flex", flexShrink: 0 }}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              animation: "spin 1s linear infinite",
            }}
          >
            <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
            <path
              d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83"
              stroke="#3B82F6"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      );
    } else if (status === "pending") {
      // Use the same blue spinner as "running"
      return (
        <div title="Pending" style={{ display: "flex", flexShrink: 0 }}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              animation: "spin 1s linear infinite",
            }}
          >
            <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
            <path
              d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83"
              stroke="#3B82F6"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      );
    } else if (status === "draft" || status === "reviewbp" || status === "reviewp" || status === "reviewpers" || status === "reviewprompts") {
      return (
        <div title="Needs review - Click to configure" style={{ display: "flex", flexShrink: 0 }}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="12" cy="12" r="10" stroke="#F59E0B" strokeWidth="2" fill="none" />
            <path
              d="M12 8v4m0 4h.01"
              stroke="#F59E0B"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      );
    }
    return null;
  };

  return (
    <main
      className="section-lines"
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: brand.subtle,
        color: brand.text,
        padding: 24,
      }}
    >
      {/* Processing notification banner */}
      {processingDomain && (
        <div
          style={{
            position: "fixed",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            maxWidth: 600,
            width: "calc(100% - 32px)",
            padding: "14px 18px",
            background: processingDomain.status === "pending" ? "rgba(254, 243, 199, 0.9)" : "rgba(219, 234, 254, 0.9)",
            backdropFilter: 'blur(12px)',
            border: `0.5px solid ${processingDomain.status === "pending" ? "rgba(245, 158, 11, 0.3)" : "rgba(59, 130, 246, 0.3)"}`,
            borderRadius: 14,
            display: "flex",
            alignItems: "center",
            gap: 12,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 100,
          }}
        >
          {/* Icon */}
          <div style={{ display: "flex", flexShrink: 0 }}>
            {processingDomain.status === "pending" ? (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  animation: "spin 1s linear infinite",
                }}
              >
                <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
                <path
                  d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83"
                  stroke="#3B82F6"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  animation: "spin 1s linear infinite",
                }}
              >
                <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
                <path
                  d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83"
                  stroke="#3B82F6"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>

          {/* Message */}
          <div style={{ flex: 1 }}>
            <p
              style={{
                margin: 0,
                fontSize: 14,
                fontWeight: 600,
                color: processingDomain.status === "pending" ? "#92400E" : "#1E40AF",
                lineHeight: 1.5,
              }}
            >
              Your domain is being analyzed. This process can take up to
              <strong>{processingDomain.status === "pending" ? " 5" : " 30"} minutes</strong>.
            </p>
          </div>
        </div>
      )}

      {/* Top-left brand */}
      <div style={{ position: "absolute", top: 16, left: 20, display: "flex", alignItems: "center", gap: 8, zIndex: 50 }}>
        <Image src="/Refinea/favicon.ico" alt="Refinea" width={24} height={24} />
        <strong style={{ fontSize: 18, letterSpacing: '-0.02em', color: brand.text, fontWeight: 800 }}>Refinea</strong>
      </div>

      {/* Top-right user info & dropdown */}
      <div style={{ position: "absolute", top: 16, right: 20, display: "flex", alignItems: "center", gap: 12, zIndex: 80 }} ref={userMenuRef}>

        {/* User avatar trigger */}
        <button
          type="button"
            onClick={() => setUserMenuOpen(o => !o)}
          title={user?.email || user?.name}
          style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            background: brand.color,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: 14,
            letterSpacing: 0.5,
            boxShadow: "0 2px 8px rgba(108, 71, 255, 0.2)",
            border: "none",
            cursor: "pointer",
            outline: "none",
            transition: 'transform 0.15s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
        >
          {(user?.name || user?.email || "U").split(/[\s@._-]+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase()}
        </button>

        {userMenuOpen && (
          <div
            style={{
              position: "absolute",
              top: 56,
              right: 0,
              background: "#ffffff",
              borderRadius: 14,
              padding: 14,
              width: 240,
              boxShadow: "0 16px 48px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.06)",
              display: "flex",
              flexDirection: "column",
              gap: 10,
              border: '0.5px solid rgba(0, 0, 0, 0.08)',
              animation: "fadeIn 0.15s ease",
            }}
          >
            <style>{`@keyframes fadeIn { from { opacity:0; transform: translateY(-4px);} to { opacity:1; transform: translateY(0);} }`}</style>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: brand.color,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 13,
                letterSpacing: 0.5,
              }}>
                {(user?.name || user?.email || "U").split(/[\s@._-]+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: brand.text, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name || user?.email}</div>
                <div style={{ fontSize: 12, color: brand.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email}</div>
              </div>
            </div>
            <div style={{ fontSize: 12, color: brand.muted }}>
              Account type: <strong style={{ color: brand.text }}>{user?.type || 'individual'}</strong>
            </div>
            <button
              onClick={logout}
              style={{
                padding: '10px 16px',
                background: brand.color,
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontWeight: 700,
                fontSize: 14,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center',
                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4m7 14l5-5-5-5m5 5H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              Logout
            </button>
          </div>
        )}
      </div>

      {/* Main two-column content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'row', gap: 48, alignItems: 'stretch', justifyContent: 'center', marginTop: 80, maxWidth: 1100, width: '100%', marginLeft: 'auto', marginRight: 'auto' }}>
        {/* Left panel: greeting + add website */}
        <div style={{ flex: '0 1 420px', maxWidth: 460, display: 'flex', flexDirection: 'column', gap: 28 }}>
          <div>
            <h1 style={{ fontSize: 44, fontWeight: 800, margin: 0, color: brand.text, lineHeight: 1.1, letterSpacing: '-0.03em' }}>
              Hello{firstName ? ` ${firstName}` : ''}
            </h1>
            <p style={{ margin: '0px 0 4px', fontSize: 16, fontWeight: 500, color: 'rgba(0, 0, 0, 0.45)', letterSpacing: '-0.01em' }}>Welcome back to Refinea.</p>
            <p style={{ marginTop: 20, fontSize: 14, color: 'rgba(0, 0, 0, 0.4)', lineHeight: 1.7, letterSpacing: '-0.01em' }}>
              Manage and analyze your websites. Add a new one to start generating GEO insights and tailored suggestions.
            </p>
          </div>

          {canAddMore && !showAddWebsite && (
            <button
              onClick={() => setShowAddWebsite(true)}
              style={{
                padding: '12px 20px',
                borderRadius: 10,
                border: 'none',
                background: brand.color,
                color: '#fff',
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: '0 2px 8px rgba(108, 71, 255, 0.25)',
                letterSpacing: '-0.01em',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(108, 71, 255, 0.35)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(108, 71, 255, 0.25)'; }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 5v14m-7-7h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              Add a new website
            </button>
          )}

          {showAddWebsite && canAddMore && (
            <div style={{ background: '#ffffff', borderRadius: 16, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)', padding: 22, border: '0.5px solid rgba(0, 0, 0, 0.06)', position: 'relative' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, letterSpacing: '-0.02em' }}>Add Website</h2>
                <button
                  onClick={() => setShowAddWebsite(false)}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 14, color: brand.muted, fontWeight: 600 }}
                >
                  Close ✕
                </button>
              </div>
              <FirstWebsiteForm userEmail={user?.email} isFirst={false} />
            </div>
          )}

          {!canAddMore && user?.type !== 'admin' && (
            <div style={{ background: 'rgba(240, 249, 255, 0.8)', padding: '12px 16px', borderRadius: 10, border: '0.5px solid rgba(186, 230, 253, 0.5)', fontSize: 13, fontWeight: 500, color: '#0369A1', letterSpacing: '-0.01em' }}>
              Limit reached ({maxWebsites} website{maxWebsites > 1 ? 's' : ''}). {user?.type === 'individual' && 'Upgrade to add more.'}
            </div>
          )}
        </div>

        {/* Right panel: website list */}
        <div style={{ flex: '1 1 540px', maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ background: '#ffffff', borderRadius: 16, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 10, border: '0.5px solid rgba(0, 0, 0, 0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, letterSpacing: '-0.02em' }}>Your projects</h2>
                <p style={{ fontSize: 13, color: 'rgba(0, 0, 0, 0.4)', margin: '4px 0 0', fontWeight: 500, letterSpacing: '-0.01em' }}>
                  {user?.type === 'admin'
                    ? `${websites?.length || 0} website${(websites?.length || 0) !== 1 ? 's added' : ''}`
                    : `${websites?.length || 0} of ${maxWebsites} website${maxWebsites > 1 ? 's added' : ''}`}
                </p>
              </div>
            </div>

            {/* Scrollable list limited to 5 visible items */}
            <div style={{ flex: 1, border: '0.5px solid rgba(0, 0, 0, 0.06)', borderRadius: 12, overflow: 'hidden', background: 'rgba(0, 0, 0, 0.01)' }}>
              <div style={{ maxHeight: WEBSITE_ROW_HEIGHT * 5, overflowY: 'auto', scrollbarWidth: 'thin' }}>
                {(websiteItems || []).length === 0 && (
                  <div style={{ padding: 32, textAlign: 'center', fontSize: 14, color: brand.muted }}>
                    No websites yet. Use the button on the left to add your first one.
                  </div>
                )}
                {(websiteItems || []).map((item, index) => {
                  const w = item.name;
                  const label = w.replace(/^https?:\/\//, "");
                  const hostname = (() => {
                    try {
                      const href = /^https?:\/\//.test(w) ? w : `https://${w}`;
                      return new URL(href).hostname;
                    } catch {
                      return label.split("/")[0];
                    }
                  })();
                  const favicon = `/api/favicon?domain=${encodeURIComponent(hostname)}&sz=32&v=2&owner=1`;
                  const isRunning = item.status === 'running';
                  const isPending = item.status === 'pending';
                  const isDraft = item.status === 'draft';
                  const isReviewPers = item.status === 'reviewpers' || item.status === 'reviewbp';
                  const isReviewPrompts = item.status === 'reviewprompts' || item.status === 'reviewp';
                  const isClickable = !isRunning && !isPending;
                  
                  // Friendly status label
                  let statusLabel = item.status || 'Ready';
                  if (item.status === 'reviewbp') statusLabel = 'Review Buyer Personas';
                  if (item.status === 'reviewp') statusLabel = 'Review Prompts';
                  if (item.status === 'reviewpers') statusLabel = 'Review Buyer Personas';
                  if (item.status === 'reviewprompts') statusLabel = 'Review Prompts';

                  return (
                    <button
                      key={item.id || w}
                      disabled={!isClickable}
                      onClick={() => {
                        if (!isClickable) return;
                        if (isDraft && item.id) {
                          router.push(`/domain/${item.id}`);
                          return;
                        }
                        if (isReviewPers && item.id) {
                          router.push(`/domain/${item.id}/personas`);
                          return;
                        }
                        if (isReviewPrompts && item.id) {
                          router.push(`/domain/${item.id}/prompts`);
                          return;
                        }
                        goTo(w);
                      }}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 14,
                        padding: '18px 24px',
                        border: 'none',
                        borderBottom: index < (websiteItems || []).length - 1 ? '0.5px solid rgba(0, 0, 0, 0.06)' : 'none',
                        background: 'transparent',
                        cursor: isClickable ? 'pointer' : 'not-allowed',
                        textAlign: 'left',
                        opacity: !isClickable ? 0.5 : 1,
                        transition: 'background 0.15s ease',
                      }}
                      onMouseEnter={(e) => { if (isClickable) e.currentTarget.style.background = 'rgba(0, 0, 0, 0.02)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <div style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 10, background: 'rgba(108, 71, 255, 0.05)', flexShrink: 0 }}>
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src={favicon} width={24} height={24} alt="" aria-hidden style={{ borderRadius: 4 }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 15, color: brand.text, marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', letterSpacing: '-0.01em' }}>{label}</div>
                        <div style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.35)', textTransform: 'capitalize', fontWeight: 500 }}>{statusLabel}</div>
                      </div>
                      <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
                        <StatusIndicator status={item.status} />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          {/* Processing / status hint when a domain is being analyzed */}
          {processingDomain && (
            <div style={{ fontSize: 12, color: brand.muted, textAlign: 'right' }}>
              Domain <strong>{processingDomain.name}</strong> is {processingDomain.status}. Refreshes automatically.
            </div>
          )}
        </div>
      </div>
      {/* Footer links */}
      <div style={{ marginTop: 40, display: 'flex', justifyContent: 'center', gap: 20, alignItems: 'center', paddingTop: 16 }}>
        {/* Gradient separator line */}
        <div style={{ position: 'absolute', left: '25%', right: '25%', height: 1, background: 'linear-gradient(to right, transparent, rgba(108, 71, 255, 0.1), transparent)', marginTop: -16 }} />
        <a
          href={
            `mailto:info@refinea.io?subject=${encodeURIComponent('Request a call with Refinea')}&body=${encodeURIComponent(
              [
                'Hello Refinea team,',
                '',
                'I would like to schedule a call to discuss:',
                '- [Brief topic or questions]',
                '',
                'My availability:',
                '- [Days/times and timezone]',
                '',
                'Company/Website:',
                '- [Your company or website]',
                '',
                'Thanks,',
                `${user?.name || '[Your name]'}`
              ].join('\n')
            )}`
          }
          style={{ color: brand.color, fontWeight: 600, textDecoration: 'none', fontSize: 13, letterSpacing: '-0.01em', transition: 'opacity 0.15s' }}
          target="_blank"
          rel="noopener noreferrer"
        >
          Contact us
        </a>
        <span style={{ color: 'rgba(0, 0, 0, 0.15)' }}>|</span>
        <a
          href="https://refinea.io"
          style={{ color: brand.color, fontWeight: 600, textDecoration: 'none', fontSize: 13, letterSpacing: '-0.01em', transition: 'opacity 0.15s' }}
          target="_blank"
          rel="noopener noreferrer"
        >
          Discover more about Refinea
        </a>
      </div>
    </main>
  );
}
